import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  captureIntakeFailure,
  currentIntakeRetryAt,
  retryableIntakeFailure,
} from "../utils/intakeLimits";

/** Keep a rejected selection only in memory and only for its original listing.
 * @param {{targetKey: string, upload: (files: File[]) => any, onSuccess?: (data: any) => void}} options
 */
const useIntakeUpload = ({ targetKey, upload, onSuccess }) => {
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const [busy, setBusy] = useState(false);
  const current = useRef({ targetKey, upload, onSuccess });
  current.current = { targetKey, upload, onSuccess };
  const mounted = useRef(false);
  const operation = useRef(/** @type {object | null} */ (null));
  const selectionTarget = useRef(targetKey);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      operation.current = null;
    };
  }, []);
  useLayoutEffect(() => {
    if (selectionTarget.current === targetKey) return;
    selectionTarget.current = targetKey;
    operation.current = null;
    setFiles([]);
    setBusy(false);
  }, [targetKey]);

  const start = useCallback(async (/** @type {File[]} */ selected) => {
    if (
      !mounted.current ||
      operation.current ||
      currentIntakeRetryAt() > Date.now() ||
      !selected.length
    )
      return null;
    const token = {};
    const target = current.current.targetKey;
    const captured = [...selected];
    operation.current = token;
    setBusy(true);
    setFiles([]);
    let result;
    try {
      result = current.current.upload(captured);
      if (result && typeof result.then === "function") result = await result;
    } catch (failure) {
      const error = /** @type {any} */ (failure);
      result = {
        ok: false,
        status: error?.response?.status,
        error: captureIntakeFailure(error) || error?.response?.data || error?.message,
      };
    }
    if (!mounted.current || operation.current !== token || current.current.targetKey !== target)
      return null;
    operation.current = null;
    setBusy(false);
    if (result?.ok === false && retryableIntakeFailure(result.error, result.status))
      setFiles(captured);
    if (result?.ok === true) current.current.onSuccess?.(result.data);
    return result;
  }, []);

  return { files, busy, start, retry: () => start(files), discard: () => setFiles([]) };
};

export default useIntakeUpload;
